/**
 * Reads and validates the public Vite environment variables.
 *
 * Throws at module-import time when a required variable is missing so we fail
 * fast at startup instead of producing inscrutable errors when the Supabase
 * client is later asked to make a request.
 */

const requireEnv = (name: string): string => {
  const value = import.meta.env[name];
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(
      `Missing required environment variable: ${name}. ` +
        `Copy .env.example to .env and run \`supabase status\` to fill in the local values.`,
    );
  }
  return value;
};

const optionalEnv = (name: string): string | undefined => {
  const value = import.meta.env[name];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

export const env = {
  supabaseUrl: requireEnv('VITE_SUPABASE_URL'),
  supabaseAnonKey: requireEnv('VITE_SUPABASE_ANON_KEY'),
  siteUrl: import.meta.env.VITE_SITE_URL ?? window.location.origin,
  // Telemetry config — optional. When unset, the corresponding client
  // is left uninitialised and observability calls become no-ops.
  sentryDsn: optionalEnv('VITE_SENTRY_DSN'),
  posthogKey: optionalEnv('VITE_POSTHOG_KEY'),
  posthogHost: optionalEnv('VITE_POSTHOG_HOST'),
  // Set by release.yml from the commit SHA so Sentry events tag-match
  // the source-map upload bound to the same SHA. Optional in dev.
  release: optionalEnv('VITE_RELEASE'),
} as const;
